// ===========================================
// Cancel ONE occurrence of a class, from the class detail page
// ===========================================
// The admin picks WHICH week to cancel on a term-week tile picker (the same
// picker the reschedule and replacement-teacher dialogs use). Until 2026-09-06
// this dialog silently cancelled "the next occurrence", computed from today's
// weekday with no regard for the term, the lesson's session weeks or existing
// exceptions — so an admin cancelling a band's 23 Sep rehearsal cancelled 9 Sep
// instead (found on the Google sync staging walkthrough, check 5).
//
// Rules:
//  • only weeks the lesson actually runs are selectable (`sessionWeeksFor`);
//  • already-cancelled weeks are disabled and listed, so one cancellation no
//    longer blocks the whole dialog;
//  • the default selection is the next upcoming session, which is what the old
//    behaviour was trying to be — but it is now SHOWN, with its date, and can be
//    changed;
//  • the date sent as `originalDate` is the RECURRING occurrence date for that
//    week (Monday-anchored week × the lesson's weekday) — the exception's
//    identity key — even for an occurrence that was rescheduled earlier.
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useCancelSingleOccurrence, useLesson, useLessonExceptions } from '../../../hooks/useLessons';
import WeekTilePicker from '../../booking/WeekTilePicker';
import {
  currentWeekNumber,
  formatUTCDate,
  occurrenceDateForWeek,
  parseUTCDate,
  startOfTodayUTC,
  termWeekCount,
  lessonWeekWindow,
  formatTermWeekDate,
} from '../../../utils/dateUtils';
import { useSessionWeeks } from './sessionWeeks';
import { enrollmentsForOccurrence } from '../../../utils/occurrenceRoster';

interface CancelClassDialogProps {
  open: boolean;
  lessonId: string;
  onClose: () => void;
  onSubmit?: () => void;
}

const MAX_REASON_LENGTH = 500;

/** `YYYY-MM-DD` of a UTC-midnight date — the backend's `originalDate` wire format. */
function toDateKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function CancelClassDialog({
  open,
  lessonId,
  onClose,
  onSubmit,
}: CancelClassDialogProps) {
  const [reason, setReason] = useState<string>('');
  const [notifyParents, setNotifyParents] = useState<boolean>(true);
  const [activateMakeup, setActivateMakeup] = useState<boolean>(false);
  const [selectedWeek, setSelectedWeek] = useState<number>(0);
  const { mutate: cancelClass, isPending: isCancelling } = useCancelSingleOccurrence();
  const { data: lesson } = useLesson(open ? lessonId : '');
  const { data: exceptions } = useLessonExceptions(open ? lessonId : undefined);

  // Releasing a credit is no longer gated on lesson type (Andrew/MNM, 2026-09-11) — a
  // cancelled GROUP class is exactly the case MNM was handling by hand. Two facts still
  // shape the COPY, because they change what the admin can then do with the credit:
  //   • how many students are about to receive one (a group cancellation grants several);
  //   • whether the class has an instrument — the parent-facing slot picker is
  //     instrument-driven, so without one the credit is admin-redeemable only.
  // ⚠️ Counted from the ENROLMENT ARRAY, not `_count.LessonEnrollment`. The include
  // filters the array to `isActive: true` but a relation `where` does NOT propagate to
  // `_count`, so `_count` still counts withdrawn students (unenrolment is a soft delete;
  // production carries 55 inactive rows). The backend grants to active enrolments only,
  // so `_count` would promise more credits than it releases. Wider `_count` overcount is
  // pre-existing and logged separately — do not "simplify" this back to `_count`.
  // Mid-term start week (Phase 4): only the students who HAVE the chosen occurrence are
  // released a credit (the backend filters by each student's start), so count the same
  // set. With no week chosen yet this fails open to the whole active roll.
  const enrolledCount = lesson?.LessonEnrollment
    ? enrollmentsForOccurrence(lesson, lesson.LessonEnrollment, selectedWeek || null).length
    : null;
  const hasInstrument = !!lesson?.instrumentId;
  const termStartDate = lesson?.Term?.startDate;
  const termEndDate = lesson?.Term?.endDate;
  const dayOfWeek = lesson?.dayOfWeek ?? null;
  const canPick = !!termStartDate && !!termEndDate && dayOfWeek !== null;

  const lastWeek = useMemo(
    () => (canPick ? termWeekCount(termStartDate!, termEndDate!) : 0),
    [canPick, termStartDate, termEndDate]
  );
  const currentWeek = useMemo(() => (termStartDate ? currentWeekNumber(termStartDate) : 0), [termStartDate]);

  // Weeks the lesson runs (null = every term week).
  const sessionWeeks = useSessionWeeks(lesson);

  // The class's own window (mid-term start / last week). `sessionWeeks` stays null for
  // a regular class on purpose, so the window travels separately.
  const classWindow = useMemo(
    () => (canPick && lesson ? lessonWeekWindow(lesson, lastWeek) : null),
    [canPick, lesson, lastWeek]
  );

  // Weeks that already carry ANY exception, by their RECURRING occurrence week. The
  // backend refuses to cancel an occurrence that has one ("An exception already exists
  // for this occurrence", 409) — cancelled, MOVED or COVERED alike — so every such week
  // is disabled here and listed with its reason, rather than offered and then refused.
  const exceptionWeeks = useMemo(() => {
    const weeks = new Map<number, 'CANCELLED' | 'RESCHEDULED' | 'TEACHER_REPLACED' | string>();
    if (!termStartDate) return weeks;
    for (const exc of exceptions ?? []) {
      const week = currentWeekNumber(termStartDate, parseUTCDate(exc.originalDate));
      // A window SHRINK keeps its exception rows, so some name weeks the class no longer
      // runs. Listing them under "already changed" describes weeks that do not exist.
      if (classWindow && (week < classWindow.first || week > classWindow.last)) continue;
      weeks.set(week, exc.type);
    }
    return weeks;
  }, [exceptions, termStartDate, classWindow]);

  // What the picker may offer: session weeks (or every week) minus those with an exception.
  const selectableWeeks = useMemo(() => {
    if (!canPick) return [];
    const base = sessionWeeks ?? Array.from({ length: lastWeek }, (_, i) => i + 1);
    return base.filter(
      (w) =>
        !exceptionWeeks.has(w) &&
        (!classWindow || (w >= classWindow.first && w <= classWindow.last))
    );
  }, [canPick, sessionWeeks, lastWeek, exceptionWeeks, classWindow]);

  const occurrenceDate = (week: number) => occurrenceDateForWeek(parseUTCDate(termStartDate!), week, dayOfWeek!);

  // Default to the next upcoming session (occurrence date on/after today, UTC — the
  // same rule WeekTilePicker uses for "past"). Re-run when the dialog opens.
  useEffect(() => {
    if (!open) {
      setSelectedWeek(0);
      return;
    }
    if (!canPick || selectedWeek) return;
    const today = startOfTodayUTC();
    const next = selectableWeeks.find((w) => occurrenceDate(w) >= today);
    if (next) setSelectedWeek(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canPick, selectableWeeks]);

  // Nothing left to cancel: every selectable week is behind us (or already changed).
  // Say so - a picker with every tile disabled and a disabled button explains nothing.
  const noRemainingWeeks =
    canPick && !!lesson && !selectableWeeks.some((w) => occurrenceDate(w) >= startOfTodayUTC());
  const lastWeekPassed =
    noRemainingWeeks && classWindow
      ? occurrenceDate(classWindow.last) < startOfTodayUTC()
      : false;

  const selectedDateLabel =
    canPick && selectedWeek
      ? formatUTCDate(occurrenceDate(selectedWeek), { weekday: 'long', day: 'numeric', month: 'long' })
      : '';

  const exceptionSummary = useMemo(() => {
    if (!canPick || exceptionWeeks.size === 0) return '';
    const label = (type: string) => (type === 'CANCELLED' ? 'cancelled' : type === 'RESCHEDULED' ? 'moved' : type === 'TEACHER_REPLACED' ? 'covered' : type.toLowerCase());
    return [...exceptionWeeks.entries()]
      .sort(([a], [b]) => a - b)
      .map(([w, type]) => `Wk ${w} (${formatUTCDate(occurrenceDate(w), { weekday: 'short', day: 'numeric', month: 'short' })}) — ${label(type)}`)
      .join(', ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPick, exceptionWeeks, termStartDate, dayOfWeek]);

  const resetForm = () => {
    setReason('');
    setNotifyParents(true);
    setActivateMakeup(false);
    setSelectedWeek(0);
  };

  const handleSubmit = () => {
    if (!canPick || !selectedWeek) return;
    cancelClass(
      {
        lessonId,
        input: {
          originalDate: toDateKey(occurrenceDate(selectedWeek)),
          reason: reason || undefined,
          notifyParents,
          activateMakeupBooking: activateMakeup ? true : undefined,
        },
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
          onSubmit?.();
        },
      }
    );
  };

  const handleClose = () => {
    if (!isCancelling) {
      resetForm();
      onClose();
    }
  };

  const submitDisabled = isCancelling || !canPick || !selectedWeek;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel Class</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {!canPick ? (
            <Alert severity="error">
              This class has no term dates or weekday on record, so a date cannot be chosen. Cancel it from the calendar instead.
            </Alert>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Which week?
              </Typography>
              <WeekTilePicker
                termStartDate={termStartDate!}
                termEndDate={termEndDate!}
                selectedWeek={selectedWeek}
                onWeekSelect={setSelectedWeek}
                currentWeek={currentWeek}
                occurrenceDayOfWeek={dayOfWeek!}
                sessionWeeks={selectableWeeks}
                windowFirst={classWindow?.first}
                windowLast={classWindow?.last}
              />
              {noRemainingWeeks && (
                <Alert severity="info" data-testid="no-remaining-weeks">
                  {lastWeekPassed && classWindow
                    ? `This class has no remaining weeks to cancel — its last week was ${formatTermWeekDate(termStartDate!, classWindow.last, dayOfWeek)}.`
                    : 'This class has no remaining weeks to cancel — every remaining week has already been changed.'}
                </Alert>
              )}
              {exceptionSummary && (
                <Typography variant="caption" color="text.secondary">
                  Not selectable (already changed): {exceptionSummary}
                </Typography>
              )}
              {selectedWeek ? (
                <Alert severity="warning">
                  This will cancel <strong>{selectedDateLabel}</strong> (week {selectedWeek})
                  {notifyParents ? ' and notify parents.' : '.'}
                  {/* Until 2026-09-11 this promised "a makeup session will be scheduled
                      shortly" unconditionally — regardless of the checkbox below, and for
                      classes that got no make-up at all. The make-up is what the checkbox
                      does, so it is described there, not here. */}
                </Alert>
              ) : (
                <Alert severity="info">Pick the week to cancel. Only weeks this class runs are selectable.</Alert>
              )}
            </>
          )}

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Cancellation Reason (Optional)"
            placeholder="Explain why this class is being cancelled (e.g., teacher illness, facility unavailable, etc.)"
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
            disabled={isCancelling || !canPick}
            helperText={`${reason.length}/${MAX_REASON_LENGTH} characters`}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={notifyParents}
                onChange={(e) => setNotifyParents(e.target.checked)}
                disabled={isCancelling || !canPick}
              />
            }
            label="Notify parents of this cancellation"
          />

          <>
            <Divider sx={{ my: 1 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Makeup Lesson Options
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={activateMakeup}
                    onChange={(e) => setActivateMakeup(e.target.checked)}
                    disabled={isCancelling || !canPick}
                  />
                }
                label="Release a make-up credit to each enrolled student"
              />
              {activateMakeup && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  {enrolledCount !== null
                    ? `${enrolledCount} student${enrolledCount === 1 ? '' : 's'} will each receive one make-up credit. `
                    : 'Each enrolled student will receive one make-up credit. '}
                  You can redeem it for them as an extra 1:1 when booking an extra class
                  {hasInstrument
                    ? ', or they can book a make-up slot themselves if the teacher has availability set.'
                    : '. This class has no instrument set, so parents cannot self-book a slot — redeem it for them.'}
                </Alert>
              )}
            </Box>
          </>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCancelling}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitDisabled}
          endIcon={isCancelling ? <CircularProgress size={20} /> : undefined}
          sx={{
            borderColor: '#FFAE9E',
            backgroundColor: 'transparent',
            color: '#FFAE9E',
            '&:hover': {
              backgroundColor: 'rgba(255, 174, 158, 0.08)',
              borderColor: '#FFAE9E',
            },
            '&.Mui-disabled': {
              borderColor: 'rgba(255, 174, 158, 0.3)',
              color: 'rgba(255, 174, 158, 0.3)',
            },
          }}
        >
          {isCancelling ? 'Cancelling...' : 'Cancel Class'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
